import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { getBankAccountsState, fetchBankAccounts } from "state/slices/bank-accounts-slice.ts";
import { showGlobalLoadingOverlay, hideGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { getPaynotePlaidLinkPath } from "utils/backend-path-builders.ts";
import { toast } from "react-toastify";
import { BankAccount, BankTransferProvider, PLAID_ITEM_STATUS_HEALTHY } from "interfaces/commerce/bank-account.ts";
import { usePlaidBankLink } from "hooks/usePlaidBankLink.ts";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import BlockyLightText from "components/custom-texts/blocky-light-text.tsx";
import PaymentMethodItem from "./payment-method-item.tsx";
import AddPaymentMethodButton from "./add-payment-method-button.tsx";
import { sparketLightGray, EDIT_PROFILE_PATH } from "utils/constants.ts";
import { useNavigate } from "react-router-dom";
import { getVerificationStatus } from "state/slices/user-slice.ts";
import { IDENTITY_VERIFICATION_STATUS } from "interfaces/users-response";

interface BankAccountSelectorProps {
  provider: BankTransferProvider;
  selectedMethod?: BankAccount;
  onMethodSelect: (method: BankAccount) => void;
  onClose?: () => void;
  returnUrl: string;
}

const needsReconnect = (account: BankAccount) =>
  account.provider === "PLAID" && account.status !== PLAID_ITEM_STATUS_HEALTHY;

const BankAccountSelector = ({
  provider,
  selectedMethod,
  onMethodSelect,
  onClose,
  returnUrl
}: BankAccountSelectorProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const bankAccountsState = useAppSelector(getBankAccountsState);
  const verificationStatus = useAppSelector(getVerificationStatus);
  const [preparingPlaidRedirect, setPreparingPlaidRedirect] = useState(false);
  const { startLink: startPlaidLink, linking: plaidLinking } = usePlaidBankLink({
    returnUrl,
    onLinked: onMethodSelect,
  });

  const isIdentityVerified = verificationStatus === IDENTITY_VERIFICATION_STATUS.APPROVED;
  const accounts = useMemo(
    () => bankAccountsState.accounts.filter(account => account.provider === provider),
    [bankAccountsState.accounts, provider]
  );

  useEffect(() => {
    if (bankAccountsState.fetchStatusState === "idle") {
      dispatch(fetchBankAccounts());
    }
  }, [dispatch, bankAccountsState.fetchStatusState]);

  // Auto-select first payment method when accounts are loaded and no method is selected
  useEffect(() => {
    if (
      bankAccountsState.fetchStatusState === "succeeded" &&
      accounts.length > 0 &&
      !selectedMethod
    ) {
      onMethodSelect(accounts[0]);
    }
  }, [bankAccountsState.fetchStatusState, accounts, selectedMethod, onMethodSelect]);

  const handleAccountSelect = (accountId: string) => {
    const selectedAccount = accounts.find(account => account.id === accountId);
    if (selectedAccount) {
      onMethodSelect(selectedAccount);
    }
  };

  const handleAddNewBankAccount = async () => {
    if (!isIdentityVerified) {
      onClose?.();
      navigate(`${EDIT_PROFILE_PATH}?returnTo=${returnUrl}`);
      return;
    }

    if (provider === "PLAID") {
      startPlaidLink();
      return;
    }

    setPreparingPlaidRedirect(true);
    try {
      dispatch(showGlobalLoadingOverlay({ message: "Setting up bank account connection..." }));
      
      const response = await postRequest(getPaynotePlaidLinkPath(), {
        body: {
          returnUrl: returnUrl
        }
      });
      const { plaid_link } = response;

      if (plaid_link) {
        window.location.href = plaid_link;
      } else {
        throw new Error('No Plaid link received from server');
      }
    } catch (error: any) {
      console.error('Error setting up bank account:', error);
      toast.error('Failed to set up bank account connection. Please try again.');
      setPreparingPlaidRedirect(false);
      dispatch(hideGlobalLoadingOverlay());
    }
  };

  // Loading state with polling consideration
  if (bankAccountsState.fetchStatusState === "loading" && !bankAccountsState.isPolling) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "120px" }}>
        <Spinner animation="border" variant="light" size="sm" />
      </div>
    );
  }

  return (
    <div>
      {accounts.length > 0 || bankAccountsState.isPolling ? (
        <div className="mb-3">
          {accounts.map((source, index) => {
            const isSelected = selectedMethod?.id === source.id || (index === 0 && !selectedMethod);
            
            return (
              <PaymentMethodItem
                key={source.id}
                id={source.id}
                isSelected={isSelected}
                onClick={(id) => handleAccountSelect(id)}
                radioName="achMethod"
              >
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  width: "100%"
                }}>
                  <div style={{ flex: "1", minWidth: "0" }}>
                    <BlockyBoldText style={{ 
                      color: "white", 
                      fontSize: "14px", 
                      fontWeight: "500",
                      lineHeight: "1.2"
                    }}>
                      {source.bankName}
                    </BlockyBoldText>
                    {needsReconnect(source) && (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{ color: "#ffc107", fontSize: "12px" }}
                        disabled={plaidLinking}
                        onClick={(e) => {
                          e.stopPropagation();
                          startPlaidLink(source.id);
                        }}
                      >
                        Reconnect
                      </button>
                    )}
                  </div>
                  <div style={{ flex: "0 0 auto", marginLeft: "12px" }}>
                    <BlockyBoldText style={{ 
                      color: "white", 
                      fontSize: "14px", 
                      fontWeight: "500",
                      lineHeight: "1.2"
                    }}>
                      •••• {source.last4}
                    </BlockyBoldText>
                  </div>
                  <div style={{ flex: "0 0 auto", marginLeft: "12px", minWidth: "60px", textAlign: "right" }}>
                    <span style={{ 
                      color: sparketLightGray, 
                      fontSize: "12px",
                      fontWeight: "500"
                    }}>
                      {source.type}
                    </span>
                  </div>
                </div>
              </PaymentMethodItem>
            );
          })}
          
          {/* Show polling state as a loading payment method */}
          {bankAccountsState.isPolling && (
            <div
              className="p-3 mb-2 rounded"
              style={{ 
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                cursor: 'not-allowed',
                border: "1px solid rgba(255, 255, 255, 0.1)"
              }}
            >
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm text-light me-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div>
                  <BlockyBoldText style={{ color: "white", fontSize: "14px" }}>
                    Setting up your new bank account...
                  </BlockyBoldText>
                  <BlockyLightText style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "12px" }}>
                    This usually takes a few moments
                  </BlockyLightText>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-3">
          <BlockyLightText style={{ color: "white" }}>
            You don't have any bank accounts.
          </BlockyLightText>
        </div>
      )}
      
      {provider !== "DISABLED" && (
        <AddPaymentMethodButton
          onClick={handleAddNewBankAccount}
          text={isIdentityVerified ? "Add Bank Account" : "Add Bank Account (ID Required)"}
          disabled={preparingPlaidRedirect || plaidLinking}
        />
      )}
    </div>
  );
};

export default BankAccountSelector; 